import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { classes, exactCurrencyFormatter } from "@/utils"
import { Check } from "lucide-react"
import { useRef, useState } from "preact/hooks"
import { Split } from "@/api"
import { CodePill } from "./code_pill.tsx"
import { ItemBadge } from "./item_badge.tsx"

export interface CodingDisplay {
  /** GL account number of the leading split — "" when the account has none. Rendered apart from the label. */
  account_number: string
  /** Account name (plus the accounting item), with "& others" appended when the splits differ. */
  label: string
  isMultiple: boolean
}

/**
 * The account number to show alongside an account name, or "" when it would be a duplicate —
 * many orgs already bake the number into the name ("491075 Trade Spend : Billback"), and
 * repeating it reads as a typo. The digit check keeps "5010" from being swallowed by "50100 Foo".
 */
export function display_account_number(account_number: string | undefined | null, name: string | undefined | null): string {
  const number = account_number?.trim() || ""
  if (!number) {
    return ""
  }
  const account_name = name?.trim() || ""
  const rest = account_name.slice(number.length)
  return account_name.startsWith(number) && !/^\d/.test(rest) ? "" : number
}

/** The account name plus the accounting item — the item is what shows up on the credit memo. */
function split_label(split: Split): string {
  const account = split.expense_account || split.name || ""
  return split.product_service ? `${account} · ${split.product_service}` : account
}

/** The account number for a split, dropped when its name already leads with it. */
function split_account_number(split: Split): string {
  return display_account_number(split.account_number, split.expense_account || split.name)
}

/**
 * Structured coding for a transaction's splits, so the cell can render the account number
 * distinctly from the account name. An empty `account_number` and `label` means uncoded.
 */
export function coding_display_parts(splits: Split[] | undefined | null): CodingDisplay {
  if (!splits || splits.length === 0) {
    return { account_number: "", label: "", isMultiple: false }
  }

  if (splits.length === 1) {
    return { account_number: split_account_number(splits[0]), label: split_label(splits[0]), isMultiple: false }
  }

  // Multiple splits - find the one with the biggest amount
  const biggestSplit = splits.reduce((max, split) =>
    Math.abs(split.amount) > Math.abs(max.amount) ? split : max
  , splits[0])

  const biggestName = biggestSplit.expense_account || biggestSplit.name || ""
  return { account_number: split_account_number(biggestSplit), label: `${biggestName} & others`, isMultiple: true }
}

/**
 * Flat coding text for a transaction's splits; empty text means uncoded. This is the table's
 * accessor, so the leading account number is what the Coding column sorts and searches on.
 */
export function coding_display_text(splits: Split[] | undefined | null): { text: string; isMultiple: boolean } {
  const { account_number, label, isMultiple } = coding_display_parts(splits)
  const text = account_number && label ? `${account_number} ${label}` : account_number || label
  return { text, isMultiple }
}

/** Hover text for a coding cell — every split spelled out, so nothing hides behind "& others". */
export function coding_tooltip_text(splits: Split[] | undefined | null): string {
  if (!splits || splits.length === 0) {
    return ""
  }

  return splits
    .map(split => {
      const number = split_account_number(split)
      const label = number ? `${number} ${split_label(split)}` : split_label(split)
      // Amounts only matter when there is more than one line to tell apart.
      return splits.length > 1 ? `${label} — ${exactCurrencyFormatter(split.amount)}` : label
    })
    .join("\n")
}

export interface SelectItem {
  id: string
  name: string
  expense_account?: string
  account_number?: string
  /** Accounting item (product/service) this row posts through — a GL account can have one row per item. */
  product_service?: string
}

interface CodingCellProps {
  splits: Split[]
  ledgerAccounts: SelectItem[]
  onSelect: (match: SelectItem) => void
}

export function CodingCell({ splits, ledgerAccounts, onSelect }: CodingCellProps) {
  const [open, setOpen] = useState(false)
  const commandListRef = useRef<HTMLDivElement>(null)

  function handleSelectCode(selection: SelectItem) {
    onSelect(selection)
    setTimeout(() => {
      setOpen(false)
    }, 0)
  }

  function handleSearchChange() {
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0
    }
  }

  const { account_number, label, isMultiple } = coding_display_parts(splits)
  const currentValue = splits?.[0]

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {account_number || label ? (
          <button
            type="button"
            title={coding_tooltip_text(splits)}
            class={classes(
              "text-left text-sm hover:underline cursor-pointer min-w-[100px] whitespace-normal",
              "text-gray-900"
            )}
          >
            {/* The GL account number is what the coding actually posts to, so lead with it. */}
            {account_number ? <span class="font-mono text-xs text-gray-500 mr-1.5">{account_number}</span> : null}
            <span class={isMultiple ? "italic" : undefined}>{label}</span>
          </button>
        ) : (
          <CodePill />
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[620px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search account..." onValueChange={handleSearchChange} />
          <CommandList ref={commandListRef}>
            <CommandEmpty>No accounts found.</CommandEmpty>
            <CommandGroup>
              {ledgerAccounts.map((selection, index) => {
                const account_number = display_account_number(selection.account_number, selection.expense_account || selection.name)
                return (
                  <CommandItem
                    key={selection.id}
                    // Include the account number and item so search finds either, and so sibling rows
                    // of the same GL account (one per item) stay distinct for cmdk.
                    value={[selection.account_number, selection.name, selection.product_service].filter(Boolean).join(" ")}
                    className={`${index % 2 === 0 ? "bg-slate-50" : ""} ${currentValue?.account_id === selection.id ? "bg-blue-100" : ""}`}
                    onSelect={() => handleSelectCode(selection)}
                  >
                    <Check
                      className={classes("mr-2 h-4 w-4", currentValue?.account_id === selection.id ? "opacity-100" : "opacity-0")}
                    />
                    {account_number ? (
                      <span className="font-mono text-xs text-gray-500 mr-2 shrink-0">{account_number}</span>
                    ) : null}
                    <span className="truncate">{selection.expense_account || selection.name}</span>
                    <ItemBadge item={selection.product_service} className="ml-2 shrink-0" />
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function make_split(overrides: Partial<Split>): Split {
    return { id: "s1", account_id: "a1", amount: 100, name: "", expense_account: "", ...overrides } as Split
  }

  describe("display_account_number", () => {
    it("returns the number when the account name does not carry it", () => {
      expect(display_account_number("5015", "Retail Promotional Allowances")).toBe("5015")
    })

    it("drops the number when the account name already leads with it", () => {
      expect(display_account_number("491075", "491075 Trade Spend : Billback: Retailer: Scan")).toBe("")
      expect(display_account_number("48005", "48005 Delivery Chargebacks")).toBe("")
    })

    it("keeps the number when the name only shares a digit prefix", () => {
      expect(display_account_number("5010", "50100 Online Promotional Allowances")).toBe("5010")
    })

    it("returns empty when there is no number", () => {
      expect(display_account_number(undefined, "Spoilage")).toBe("")
      expect(display_account_number("", "Spoilage")).toBe("")
      expect(display_account_number("  ", "Spoilage")).toBe("")
    })
  })

  describe("coding_display_parts", () => {
    it("returns empty parts for no splits (uncoded)", () => {
      expect(coding_display_parts(undefined)).toEqual({ account_number: "", label: "", isMultiple: false })
      expect(coding_display_parts(null)).toEqual({ account_number: "", label: "", isMultiple: false })
      expect(coding_display_parts([])).toEqual({ account_number: "", label: "", isMultiple: false })
    })

    it("keeps the account number separate from the account name", () => {
      expect(
        coding_display_parts([make_split({ account_number: "5015", expense_account: "Retail Promotional Allowances" })])
      ).toEqual({ account_number: "5015", label: "Retail Promotional Allowances", isMultiple: false })
    })

    it("takes the account number from the biggest split for multiple splits", () => {
      const splits = [
        make_split({ account_number: "5010", expense_account: "Small", amount: 10 }),
        make_split({ account_number: "5015", expense_account: "Big", amount: -500 }),
      ]
      expect(coding_display_parts(splits)).toEqual({ account_number: "5015", label: "Big & others", isMultiple: true })
    })

    it("does not repeat a number the account name already carries", () => {
      expect(
        coding_display_parts([make_split({ account_number: "491075", expense_account: "491075 Trade Spend : Scan" })])
      ).toEqual({ account_number: "", label: "491075 Trade Spend : Scan", isMultiple: false })
    })
  })

  describe("coding_display_text", () => {
    it("returns empty text for no splits (uncoded)", () => {
      expect(coding_display_text(undefined)).toEqual({ text: "", isMultiple: false })
      expect(coding_display_text(null)).toEqual({ text: "", isMultiple: false })
      expect(coding_display_text([])).toEqual({ text: "", isMultiple: false })
    })

    it("uses expense_account over name for a single split", () => {
      expect(coding_display_text([make_split({ expense_account: "Trade Spend", name: "Other" })])).toEqual({
        text: "Trade Spend",
        isMultiple: false,
      })
      expect(coding_display_text([make_split({ name: "Other" })])).toEqual({ text: "Other", isMultiple: false })
    })

    it("leads with the account number so the column sorts and searches on it", () => {
      expect(
        coding_display_text([make_split({ account_number: "5015", expense_account: "Retail Promotional Allowances" })])
      ).toEqual({ text: "5015 Retail Promotional Allowances", isMultiple: false })
    })

    it("appends the accounting item for a single split when present", () => {
      expect(
        coding_display_text([make_split({ expense_account: "48005 Delivery Chargebacks", product_service: "Shortages (Wholesale)" })])
      ).toEqual({ text: "48005 Delivery Chargebacks · Shortages (Wholesale)", isMultiple: false })
    })

    it("shows the biggest split by absolute amount with '& others' for multiple splits", () => {
      const splits = [
        make_split({ expense_account: "Small", amount: 10 }),
        make_split({ expense_account: "Big", amount: -500 }),
      ]
      expect(coding_display_text(splits)).toEqual({ text: "Big & others", isMultiple: true })
    })
  })

  describe("coding_tooltip_text", () => {
    it("returns empty text when uncoded", () => {
      expect(coding_tooltip_text(undefined)).toBe("")
      expect(coding_tooltip_text([])).toBe("")
    })

    it("shows the full account and item for a single split, without an amount", () => {
      expect(
        coding_tooltip_text([
          make_split({ expense_account: "48005 Delivery Chargebacks", product_service: "Shortages (Wholesale)" }),
        ])
      ).toBe("48005 Delivery Chargebacks · Shortages (Wholesale)")
    })

    it("prefixes each line with the account number when the account has one", () => {
      expect(
        coding_tooltip_text([make_split({ account_number: "5015", expense_account: "Retail Promotional Allowances" })])
      ).toBe("5015 Retail Promotional Allowances")
    })

    it("spells out every split with its amount, one per line", () => {
      const splits = [
        make_split({ expense_account: "48005 Delivery Chargebacks", amount: -250.5 }),
        make_split({ expense_account: "", name: "Distributor Administration Fee Culinary", amount: -12 }),
      ]
      expect(coding_tooltip_text(splits)).toBe(
        "48005 Delivery Chargebacks — -$250.50\nDistributor Administration Fee Culinary — -$12.00"
      )
    })
  })
}
